﻿import { ANONYMOUS_PASSWORD, ANONYMOUS_USER } from '@avanta/shared/constants/common';
import { oauthToken } from '@avanta/shared/services/auth';
import { TrackingService } from 'services/tracking';
import { useUserStore } from 'stores/user';
import { type CreateTracking, type TrackingData, type TrackingEventsType } from 'types/tracking';
import { getUserId } from 'utils/storage';
import { type UseTracking } from './types';

export function useTracking(): UseTracking {
  const track = async (
    event: TrackingEventsType,
    partialData: Partial<TrackingData> = {},
    anonymousToken: boolean = false
  ): Promise<void> => {
    try {
      const storeUserId = useUserStore.getState().user?.id;
      const resolvedUserId = partialData.userId || storeUserId || getUserId() || '0';

      // Skip tracking if userId is '0' (invalid/unknown user)
      if (resolvedUserId === '0') {
        return;
      }

      const baseData: Partial<TrackingData> = {
        userId: resolvedUserId,
        timestamp: new Date().toISOString(),
      };

      const payload: CreateTracking = {
        event,
        data: {
          ...baseData,
          ...partialData,
        },
      };

      if (anonymousToken) {
        try {
          const tokenResponse = await oauthToken(ANONYMOUS_USER, ANONYMOUS_PASSWORD);
          const token = tokenResponse?.data?.access_token;

          const headers = {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept-Language': 'es',
          };

          await TrackingService.createTracking(resolvedUserId, payload, { headers });
        } catch (error) {
          throw new Error(`Failed to track with anonymous token: ${error}`);
        }
      } else {
        await TrackingService.createTracking(resolvedUserId, payload);
      }
    } catch (error) {
      throw new Error(`Failed to track: ${error}`);
    }
  };

  return { track };
}
